/**
 * 权限匹配器 - 核心层（框架无关）
 * 负责权限的匹配、过滤和权限组管理
 */

import { V6Permission } from './V6Permission'

export interface PermissionMatchResult {
  matched: boolean
  matchedPermission?: V6Permission
  reason?: string
  details: {
    industryMatch: boolean
    roleMatch: boolean
    moduleMatch: boolean
    actionMatch: boolean
  }
}

export interface PermissionFilterOptions {
  industryCode?: string
  role?: string
  module?: string
  action?: string
  includeWildcard?: boolean
}

/**
 * 权限匹配器类
 */
export class PermissionMatcher {
  /**
   * 检查用户权限是否匹配所需权限
   */
  static matchPermission(
    userPermission: V6Permission | string,
    requiredPermission: V6Permission | string
  ): PermissionMatchResult {
    const userPerm = this.normalizePermission(userPermission)
    const requiredPerm = this.normalizePermission(requiredPermission)
    
    const industryMatch = userPerm.industryCode === '*' || 
                         userPerm.industryCode === requiredPerm.industryCode
    
    const roleMatch = userPerm.role === requiredPerm.role
    
    const moduleMatch = userPerm.module === '*' || 
                       userPerm.module === requiredPerm.module
    
    const actionMatch = userPerm.action === '*' || 
                       userPerm.action === requiredPerm.action

    const matched = industryMatch && roleMatch && moduleMatch && actionMatch
    
    return {
      matched,
      matchedPermission: matched ? userPerm : undefined,
      reason: matched ? undefined : this.generateMatchFailureReason(
        industryMatch, roleMatch, moduleMatch, actionMatch
      ),
      details: {
        industryMatch,
        roleMatch,
        moduleMatch,
        actionMatch
      }
    }
  }

  /**
   * 检查用户权限列表是否匹配任意所需权限
   */
  static matchAnyPermission(
    userPermissions: (V6Permission | string)[],
    requiredPermissions: (V6Permission | string)[]
  ): PermissionMatchResult[] {
    const results: PermissionMatchResult[] = []
    
    for (const userPerm of userPermissions) {
      for (const requiredPerm of requiredPermissions) {
        const matchResult = this.matchPermission(userPerm, requiredPerm)
        if (matchResult.matched) {
          results.push(matchResult)
        }
      }
    }
    
    return results
  }

  /**
   * 检查用户权限列表是否匹配所有所需权限
   */
  static matchAllPermissions(
    userPermissions: (V6Permission | string)[],
    requiredPermissions: (V6Permission | string)[]
  ): { allMatched: boolean; results: PermissionMatchResult[] } {
    const results: PermissionMatchResult[] = []
    let allMatched = true
    
    for (const requiredPerm of requiredPermissions) {
      let matched = false
      
      for (const userPerm of userPermissions) {
        const matchResult = this.matchPermission(userPerm, requiredPerm)
        results.push(matchResult)
        
        if (matchResult.matched) {
          matched = true
          break
        }
      }
      
      if (!matched) {
        allMatched = false
      }
    }
    
    return { allMatched, results }
  }

  /**
   * 过滤权限列表
   */
  static filterPermissions(
    permissions: (V6Permission | string)[],
    options: PermissionFilterOptions
  ): V6Permission[] {
    const normalizedPermissions = permissions.map(perm => this.normalizePermission(perm))
    
    return normalizedPermissions.filter(perm => {
      // 检查行业匹配
      if (options.industryCode && 
          perm.industryCode !== '*' && 
          perm.industryCode !== options.industryCode) {
        return false
      }
      
      // 检查角色匹配
      if (options.role && perm.role !== options.role) {
        return false
      }
      
      // 检查模块匹配
      if (options.module && 
          perm.module !== '*' && 
          perm.module !== options.module) {
        return false
      }
      
      // 检查操作匹配
      if (options.action && 
          perm.action !== '*' && 
          perm.action !== options.action) {
        return false
      }
      
      // 检查是否包含通配符
      if (options.includeWildcard === false && 
          (perm.industryCode === '*' || 
           perm.module === '*' || 
           perm.action === '*')) {
        return false
      }
      
      return true
    })
  }

  /**
   * 根据用户角色和行业获取可用权限
   */
  static getAvailablePermissions(
    userPermissions: (V6Permission | string)[],
    userRole: string,
    userIndustryCode: string,
    targetModule?: string
  ): V6Permission[] {
    const normalizedPermissions = userPermissions.map(perm => this.normalizePermission(perm))
    
    return normalizedPermissions.filter(perm => {
      // 检查角色匹配
      if (perm.role !== userRole) {
        return false
      }
      
      // 检查行业匹配
      if (perm.industryCode !== '*' && perm.industryCode !== userIndustryCode) {
        return false
      }
      
      // 检查模块匹配
      if (targetModule && perm.module !== '*' && perm.module !== targetModule) {
        return false
      }
      
      return true
    })
  }

  /**
   * 检查用户是否可以访问指定行业
   */
  static canAccessIndustry(
    userPermissions: (V6Permission | string)[],
    targetIndustryCode: string
  ): boolean {
    const normalizedPermissions = userPermissions.map(perm => this.normalizePermission(perm))
    
    return normalizedPermissions.some(perm => {
      return perm.industryCode === '*' || perm.industryCode === targetIndustryCode
    })
  }

  /**
   * 检查用户是否可以执行指定操作
   */
  static canPerformAction(
    userPermissions: (V6Permission | string)[],
    userRole: string,
    userIndustryCode: string,
    targetModule: string,
    targetAction: string
  ): boolean {
    const normalizedPermissions = userPermissions.map(perm => this.normalizePermission(perm))
    
    return normalizedPermissions.some(perm => {
      // 检查角色匹配
      if (perm.role !== userRole) {
        return false
      }
      
      // 检查行业匹配
      if (perm.industryCode !== '*' && perm.industryCode !== userIndustryCode) {
        return false
      }
      
      // 检查模块匹配
      if (perm.module !== '*' && perm.module !== targetModule) {
        return false
      }
      
      // 检查操作匹配
      if (perm.action !== '*' && perm.action !== targetAction) {
        return false
      }
      
      return true
    })
  }

  /**
   * 获取用户在某模块的可用操作列表
   */
  static getAvailableActions(
    userPermissions: (V6Permission | string)[],
    userRole: string,
    userIndustryCode: string,
    targetModule: string
  ): string[] {
    const normalizedPermissions = userPermissions.map(perm => this.normalizePermission(perm))
    const actions = new Set<string>()
    
    normalizedPermissions.forEach(perm => {
      // 检查角色匹配
      if (perm.role !== userRole) {
        return
      }
      
      // 检查行业匹配
      if (perm.industryCode !== '*' && perm.industryCode !== userIndustryCode) {
        return
      }
      
      // 检查模块匹配
      if (perm.module !== '*' && perm.module !== targetModule) {
        return
      }
      
      // 添加操作
      if (perm.action === '*') {
        // 通配符操作，需要扩展为所有可用操作
        const allActions = ['view', 'create', 'edit', 'delete', 'publish', 'review', 'export']
        allActions.forEach(action => actions.add(action))
      } else {
        actions.add(perm.action)
      }
    })
    
    return Array.from(actions)
  }

  /**
   * 标准化权限对象
   */
  private static normalizePermission(permission: V6Permission | string): V6Permission {
    if (typeof permission === 'string') {
      return V6Permission.fromString(permission)
    }
    return permission
  }

  /**
   * 生成匹配失败原因
   */
  private static generateMatchFailureReason(
    industryMatch: boolean,
    roleMatch: boolean,
    moduleMatch: boolean,
    actionMatch: boolean
  ): string {
    const reasons: string[] = []
    
    if (!industryMatch) reasons.push('行业不匹配')
    if (!roleMatch) reasons.push('角色不匹配')
    if (!moduleMatch) reasons.push('模块不匹配')
    if (!actionMatch) reasons.push('操作不匹配')
    
    return reasons.join('，')
  }
}